import { useScroll } from "framer-motion";
import { throttle } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";

interface UseScrollVisibilityOptions {
  threshold?: number;
}

interface UseScrollDirectionOptions {
  threshold?: number;
}

interface UseScrollCallbackOptions {
  throttleMs?: number;
}

export function useScrollVisibility({
  threshold = 100,
}: UseScrollVisibilityOptions = {}) {
  const { scrollY } = useScroll();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const unsubscribe = scrollY.on("change", (latest) => {
      setIsVisible(latest > threshold);
    });

    return unsubscribe;
  }, [scrollY, threshold]);

  return isVisible;
}

export function useScrollDirection({
  threshold = 100,
}: UseScrollDirectionOptions = {}) {
  const [direction, setDirection] = useState<"up" | "down" | "none">("none");
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > threshold) {
        setDirection("down");
      } else if (currentScrollY < lastScrollY && currentScrollY > threshold) {
        setDirection("up");
      } else if (currentScrollY <= threshold) {
        setDirection("none");
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, threshold]);

  return { direction, scrollY: lastScrollY };
}

export function useScrollCallback(
  callback: ((scrollY: number) => void) | undefined,
  { throttleMs = 16 }: UseScrollCallbackOptions = {},
) {
  const { scrollY } = useScroll();
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  const throttledCallback = useMemo(
    () =>
      throttle((latest: number) => {
        if (callbackRef.current) {
          callbackRef.current(latest);
        }
      }, throttleMs),
    [throttleMs],
  );

  useEffect(() => {
    if (!callback) return;

    const unsubscribe = scrollY.on("change", throttledCallback);

    return unsubscribe;
  }, [scrollY, throttledCallback, callback]);
}

export function useScrollY() {
  const { scrollY } = useScroll();
  return scrollY;
}
